import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Collapse, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SaveIcon from '@mui/icons-material/Save'
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset'
import { deleteMiColorVariedad, getMisColoresVariedades, getMisVariedades, patchMiColorVariedad } from '../services/apis'
import { normalizarArray } from '../utils/apiResponse'
import { VariedadLabel } from './planner/VariedadLabel'

export const ColoresVariedades = () => {
  const [abierto, setAbierto] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [variedades, setVariedades] = useState([])
  const [colores, setColores] = useState({})
  const [borradores, setBorradores] = useState({})
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState({})
  const [error, setError] = useState('')

  useEffect(() => {
    let activo = true
    Promise.all([getMisVariedades(), getMisColoresVariedades()])
      .then(([lista, preferencias]) => {
        if (!activo) return
        setVariedades(normalizarArray(lista, 'variedades'))
        const mapa = Object.fromEntries(normalizarArray(preferencias, 'coloresVariedades').map(v => [v.codVariedad, v.colorVariedad]))
        setColores(mapa)
        setBorradores(mapa)
      })
      .catch(err => {
        if (activo) setError(err.response?.data?.message || 'No se han podido cargar los colores de variedades.')
      })
      .finally(() => { if (activo) setCargando(false) })
    return () => { activo = false }
  }, [])

  const filtradas = useMemo(() => {
    const texto = busqueda.trim().toLocaleLowerCase('es')
    return variedades.filter(v => `${v.codVariedad} ${v.desVariedad}`.toLocaleLowerCase('es').includes(texto))
  }, [variedades, busqueda])

  const guardar = async (codigo, restablecer = false) => {
    setGuardando(actual => ({ ...actual, [codigo]: true }))
    setError('')
    try {
      const color = restablecer ? '' : (borradores[codigo] || '#ffffff')
      if (restablecer) await deleteMiColorVariedad(codigo)
      else await patchMiColorVariedad(codigo, color)
      setColores(actual => ({ ...actual, [codigo]: color }))
      setBorradores(actual => ({ ...actual, [codigo]: color }))
    } catch (err) {
      const mensaje = err.response?.data?.message
      setError(Array.isArray(mensaje) ? mensaje.join('. ') : mensaje || 'No se ha podido guardar el color.')
    } finally {
      setGuardando(actual => ({ ...actual, [codigo]: false }))
    }
  }

  return (
    <Box component="section" sx={{ width: '100%', mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="h5">Colores de variedades</Typography>
        <Button variant="outlined" size="small" aria-expanded={abierto} onClick={() => setAbierto(v => !v)} startIcon={abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
          {abierto ? 'Ocultar' : 'Ver variedades'}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{String(error)}</Alert>}
      <Collapse in={abierto}>
        {cargando ? <CircularProgress size={24} sx={{ my: 2 }} /> : (
          <>
            <TextField label="Buscar variedad" size="small" value={busqueda} onChange={e => setBusqueda(e.target.value)} fullWidth sx={{ my: 1.5 }} />
            <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
              {filtradas.map(v => {
                const codigo = v.codVariedad
                const color = borradores[codigo] || '#ffffff'
                const ocupado = Boolean(guardando[codigo])
                return (
                  <Box key={codigo} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8, borderBottom: '1px solid #E2E8F0' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap variant="body2" title={v.desVariedad}><VariedadLabel color={borradores[codigo]}>{v.desVariedad}</VariedadLabel></Typography>
                      <Typography variant="caption" color="text.secondary">{codigo}</Typography>
                    </Box>
                    <Box component="input" type="color" value={color} aria-label={`Color de ${v.desVariedad}`} title="Seleccionar color" disabled={ocupado} onChange={e => setBorradores(actual => ({ ...actual, [codigo]: e.target.value }))} sx={{ flexShrink: 0, width: 48, height: 36, p: 0.3, border: '1px solid #CBD5E1', borderRadius: '6px', background: '#FFFFFF', cursor: 'pointer' }} />
                    <Tooltip title="Guardar color"><span><IconButton aria-label={`Guardar color de ${v.desVariedad}`} disabled={ocupado || !borradores[codigo] || borradores[codigo] === colores[codigo]} onClick={() => guardar(codigo)}>{ocupado ? <CircularProgress size={20} /> : <SaveIcon />}</IconButton></span></Tooltip>
                    <Tooltip title="Sin color"><span><IconButton aria-label={`Restablecer color de ${v.desVariedad}`} disabled={ocupado || !colores[codigo]} onClick={() => guardar(codigo, true)}><FormatColorResetIcon /></IconButton></span></Tooltip>
                  </Box>
                )
              })}
              {!filtradas.length && <Typography color="text.secondary" sx={{ py: 2 }}>No hay variedades.</Typography>}
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  )
}
